import {
    Alert,
    AlertDialog,
    AlertDialogBody,
    AlertDialogCloseButton,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    AlertIcon,
    Box,
    Button,
    Checkbox,
    HStack,
    Icon,
    Input,
    InputGroup,
    InputLeftElement,
    Text,
    Tr,
    VStack,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    TbArrowsExchange,
    TbCategory,
    TbCar,
    TbSearch,
    TbShieldX,
    TbTrash,
} from 'react-icons/tb';
import { HiOutlineLocationMarker } from 'react-icons/hi';
import type { PartRule, SelectionSettings } from '../../../../hooks/useGetSettings';
import type { CatalogPositionGroup } from '../../../../hooks/useGetPCDBCatalog';
import PartBayButton from '../../../../components/PartBayButton';
import Pagination from '../../../../components/Pagination';
import BulkActionBar, { BulkActionBarItem } from '../../../../components/BulkActionBar';
import { useSettingsDraft } from '../../SettingsDraftContext';
import { getPartTypeLabel } from '../../../../utils/chartUtils';
import {
    SETTINGS_TABLE_GUTTER,
    SettingsPageActions,
    SettingsRowActions,
    SettingsTable,
    SettingsTableFooter,
    SettingsTableTabs,
    SettingsTd,
    SettingsTh,
    useDynamicPageSize,
} from '../../../../components/settings/primitives';
import {
    ALL_POSITIONS,
    InlinePartTypesCell,
    InlinePositionCell,
    InlineRuleInsuranceCell,
    InlineRuleMakesCell,
} from './PartAllowanceCells';
import { usePositionOptionsByPart } from './partAllowanceDrafts';
import {
    PART_RULE_LISTS,
    partRuleListByField,
    partRuleListBySlug,
    type PartRuleField,
} from './partAllowanceLists';
import PartAllowanceBulkModal, {
    type PartAllowanceBulkMode,
    type PartAllowanceBulkValue,
} from './PartAllowanceBulkModal';
import { useSelectionDraft } from './shared';
import type { SelectionSectionProps } from './types';

const matchesSearch = (part: PartRule, term: string) =>
    part.partTerminologyName.toLowerCase().includes(term) ||
    part.positionGroupName.toLowerCase().includes(term) ||
    part.categoryName.toLowerCase().includes(term) ||
    part.subcategoryName.toLowerCase().includes(term) ||
    ((part.makes === 'all' || !part.makes) && ('all'.includes(term) || 'oem'.includes(term))) ||
    (Array.isArray(part.makes) && part.makes.some(make => make.name.toLowerCase().includes(term))) ||
    (part.allowablePartTypes?.some(apt =>
        getPartTypeLabel(apt.partType).toLowerCase().includes(term) ||
        apt.certifications?.some(certification => certification.toLowerCase().includes(term))
    ) ?? false);

const PartAllowancesSection = ({ data, oemOptions }: SelectionSectionProps) => {
    const { selection, updateSelection } = useSelectionDraft(data);
    const { isDirty } = useSettingsDraft();
    const navigate = useNavigate();
    // The open tab lives in the URL so the add-parts flow can come back to the
    // list the user was on rather than resetting to the first one.
    const [searchParams, setSearchParams] = useSearchParams();
    const [requestedTab, setRequestedTab] = useState<PartRuleField>(
        () => partRuleListBySlug(searchParams.get('list') ?? undefined)?.id ?? 'allowedPartRules',
    );
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    /** Indices into the active tab's rules, as the selection checkboxes set them. */
    const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(new Set());
    const [bulkMode, setBulkMode] = useState<PartAllowanceBulkMode | null>(null);
    /** Rules awaiting delete confirmation: one row, or the whole selection. */
    const [removeIndexes, setRemoveIndexes] = useState<number[] | null>(null);
    const removeCancelRef = useRef<HTMLButtonElement>(null);
    const { scrollRef: tableScrollRef, pageSize: partsPerPage } = useDynamicPageSize();
    const positionOptionsByPart = usePositionOptionsByPart();

    // Allowed parts only matter while OEM-only selection is enforced.
    const visibleTabs = useMemo(
        () => PART_RULE_LISTS.filter(tab => tab.id !== 'allowedPartRules' || selection.alwaysPreferOem),
        [selection.alwaysPreferOem],
    );

    const activeTab = visibleTabs.find(tab => tab.id === requestedTab) ?? visibleTabs[0];

    const rules = useMemo(
        () => selection[activeTab.id] || [],
        [selection, activeTab.id],
    );

    // Rows carry their index in the unfiltered list, so editing, removing and
    // selecting all address the same rule no matter what the search hides.
    const filteredRules = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        const indexed = rules.map((rule, index) => ({ rule, index }));
        if (!term) return indexed;
        return indexed.filter(entry => matchesSearch(entry.rule, term));
    }, [rules, searchTerm]);

    const totalPages = Math.ceil(filteredRules.length / partsPerPage);

    // The page size follows the viewport, so a resize can shrink the page
    // count; clamp so the user is never stranded on a now-empty page.
    useEffect(() => {
        setPage((prev) => Math.min(prev, Math.max(1, totalPages)));
    }, [totalPages]);

    const currentRules = useMemo(() => {
        const startIndex = (page - 1) * partsPerPage;
        return filteredRules.slice(startIndex, startIndex + partsPerPage);
    }, [filteredRules, page, partsPerPage]);

    // Search can hide a selected row; drop those so the bulk bar never acts on
    // parts that are no longer on screen.
    useEffect(() => {
        setSelectedIndexes((prev) => {
            if (prev.size === 0) return prev;
            const visible = new Set(filteredRules.map((entry) => entry.index));
            const next = new Set(Array.from(prev).filter((index) => visible.has(index)));
            return next.size === prev.size ? prev : next;
        });
    }, [filteredRules]);

    const allPageSelected =
        currentRules.length > 0 && currentRules.every((entry) => selectedIndexes.has(entry.index));
    const somePageSelected = currentRules.some((entry) => selectedIndexes.has(entry.index));
    const selectedCount = selectedIndexes.size;

    /** Position groups every selected part has, for the bulk position editor. */
    const sharedPositionGroups = useMemo<CatalogPositionGroup[]>(() => {
        const selected = Array.from(selectedIndexes)
            .map((index) => rules[index])
            .filter((rule): rule is PartRule => !!rule);
        if (selected.length === 0) return [];
        const [first, ...rest] = selected;
        const firstGroups = positionOptionsByPart.get(first.partTerminologyId)?.groups ?? [];
        return firstGroups.filter((group) =>
            rest.every((rule) =>
                (positionOptionsByPart.get(rule.partTerminologyId)?.groups ?? []).some(
                    (other) => other.groupName === group.groupName,
                ),
            ),
        );
    }, [selectedIndexes, rules, positionOptionsByPart]);

    const moveTargets = useMemo(
        () =>
            visibleTabs
                .filter((tab) => tab.id !== activeTab.id)
                .map((tab) => ({
                    id: tab.id,
                    label: tab.label,
                    description: tab.shortDescription,
                    icon: tab.icon,
                })),
        [visibleTabs, activeTab.id],
    );

    const bulkActions = useMemo<BulkActionBarItem[]>(() => {
        const actions: BulkActionBarItem[] = [
            { id: 'partTypes', label: 'Part Types', icon: TbCategory as React.ElementType },
            { id: 'makes', label: 'OEMs', icon: TbCar as React.ElementType },
            { id: 'move', label: 'Move to', icon: TbArrowsExchange as React.ElementType },
            { id: 'remove', label: 'Remove', icon: TbTrash as React.ElementType, danger: true },
            // Positions are part-specific, so this one is the least often
            // useful of the five; the bar only fits four as buttons.
            {
                id: 'position',
                label: 'Position',
                icon: HiOutlineLocationMarker as React.ElementType,
                other: true,
            },
        ];
        if (activeTab.showInsurance) {
            actions.push({
                id: 'insurance',
                label: 'Prohibited Insurance',
                icon: TbShieldX as React.ElementType,
                other: true,
            });
        }
        return actions;
    }, [activeTab.showInsurance]);

    const setRules = (field: PartRuleField, nextRules: PartRule[]) => {
        const updates: Partial<SelectionSettings> = { [field]: nextRules };
        updateSelection(updates);
    };

    const updateRuleAt = (index: number, patch: Partial<PartRule>) => {
        setRules(
            activeTab.id,
            rules.map((rule, current) => (current === index ? { ...rule, ...patch } : rule)),
        );
    };

    /** Applies one patch to every selected rule in a single draft write. */
    const patchSelected = (build: (rule: PartRule) => Partial<PartRule>) => {
        setRules(
            activeTab.id,
            rules.map((rule, index) =>
                selectedIndexes.has(index) ? { ...rule, ...build(rule) } : rule,
            ),
        );
        setSelectedIndexes(new Set());
    };

    const handleChangeTab = (id: string) => {
        setRequestedTab(id as PartRuleField);
        setSearchParams({ list: partRuleListByField(id as PartRuleField).slug }, { replace: true });
        setSearchTerm('');
        setPage(1);
        setSelectedIndexes(new Set());
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setPage(1);
    };

    const handleToggleSelect = (index: number, checked: boolean) => {
        setSelectedIndexes((prev) => {
            const next = new Set(prev);
            if (checked) next.add(index);
            else next.delete(index);
            return next;
        });
    };

    // Header checkbox covers the current page only, matching what is visible.
    const handleSelectAllOnPage = (checked: boolean) => {
        setSelectedIndexes((prev) => {
            const next = new Set(prev);
            currentRules.forEach((entry) => {
                if (checked) next.add(entry.index);
                else next.delete(entry.index);
            });
            return next;
        });
    };

    // Adding parts and reopening one rule are the same full-screen flow, which
    // saves the list itself; the inline cells above are what this section stages.
    const openFlow = (query = '') =>
        navigate(`/settings/part-allowances/${activeTab.slug}${query}`);

    const handleConfirmRemove = () => {
        if (!removeIndexes) return;
        const dropped = new Set(removeIndexes);
        setRules(activeTab.id, rules.filter((_, index) => !dropped.has(index)));
        setSelectedIndexes(new Set());
        setRemoveIndexes(null);
    };

    /**
     * Moving is one write covering two lists: the rules leave the active tab
     * and are appended to the target, minus the prohibited insurance if the
     * target does not carry it.
     */
    const handleMove = (target: PartRuleField) => {
        const moving = rules.filter((_, index) => selectedIndexes.has(index));
        if (moving.length === 0) return;
        const targetTab = partRuleListByField(target);
        const arriving = moving.map((rule) => {
            if (targetTab?.showInsurance) return rule;
            const { prohibitedInsurance, ...rest } = rule;
            return rest as PartRule;
        });
        updateSelection({
            [activeTab.id]: rules.filter((_, index) => !selectedIndexes.has(index)),
            [target]: [...(selection[target] || []), ...arriving],
        } as Partial<SelectionSettings>);
        setSelectedIndexes(new Set());
    };

    const handleBulkApply = (value: PartAllowanceBulkValue) => {
        switch (value.mode) {
            case 'position':
                patchSelected((rule) => {
                    const options = positionOptionsByPart.get(rule.partTerminologyId);
                    if (value.groupName === ALL_POSITIONS) {
                        return {
                            positionIds: options?.allPositionIds ?? rule.positionIds,
                            positionGroupName: ALL_POSITIONS,
                        };
                    }
                    const group = options?.groups.find(
                        (entry) => entry.groupName === value.groupName,
                    );
                    if (!group) return {};
                    return {
                        positionIds: group.positionIds,
                        positionGroupName: group.groupName,
                    };
                });
                break;
            case 'partTypes':
                patchSelected(() => ({ allowablePartTypes: value.partTypes }));
                break;
            case 'makes':
                patchSelected(() => ({ makes: value.makes }));
                break;
            case 'insurance':
                patchSelected(() => ({ prohibitedInsurance: value.insurance }));
                break;
            case 'move':
                handleMove(value.target as PartRuleField);
                break;
        }
        setBulkMode(null);
    };

    return (
        <>
            <SettingsPageActions>
                <PartBayButton
                    size="sm"
                    variant="solid"
                    colorScheme="partbay-blue"
                    onClick={() => openFlow()}
                    text="Add Parts"
                />
            </SettingsPageActions>

            <SettingsTableTabs
                tabs={visibleTabs.map(tab => ({
                    id: tab.id,
                    label: tab.label,
                    count: (selection[tab.id] || []).length,
                }))}
                value={activeTab.id}
                onChange={handleChangeTab}
            />

            <HStack
                justify="space-between"
                align="center"
                gap={4}
                px={SETTINGS_TABLE_GUTTER}
                pb={4}
                flexShrink={0}
            >
                <InputGroup size="sm" maxW="500px">
                    <InputLeftElement pointerEvents="none">
                        <Icon as={TbSearch as React.ElementType} color="gray.400" />
                    </InputLeftElement>
                    <Input
                        placeholder={`Search ${rules.length} part${rules.length !== 1 ? 's' : ''}...`}
                        value={searchTerm}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        bg="white"
                    />
                </InputGroup>
                <Text fontSize="sm" color="gray.600" textAlign="right" flexShrink={0}>
                    {activeTab.description}
                </Text>
            </HStack>

            {rules.length === 0 ? (
                <Box px={SETTINGS_TABLE_GUTTER} pb={5}>
                    <Alert status="info" variant="left-accent">
                        <AlertIcon />
                        <Text fontSize="sm">{activeTab.emptyText}</Text>
                    </Alert>
                </Box>
            ) : (
                <>
                    <SettingsTable
                        scrollRef={tableScrollRef}
                        head={
                            <>
                                <SettingsTh w="44px">
                                    <Checkbox
                                        size="sm"
                                        colorScheme="partbay-blue"
                                        isChecked={allPageSelected}
                                        isIndeterminate={!allPageSelected && somePageSelected}
                                        isDisabled={currentRules.length === 0}
                                        onChange={(e) => handleSelectAllOnPage(e.target.checked)}
                                        aria-label="Select all parts on this page"
                                    />
                                </SettingsTh>
                                <SettingsTh w={activeTab.showInsurance ? '22%' : '26%'}>Part</SettingsTh>
                                <SettingsTh w={activeTab.showInsurance ? '14%' : '16%'}>Position</SettingsTh>
                                <SettingsTh w={activeTab.showInsurance ? '22%' : '26%'}>Part Types</SettingsTh>
                                <SettingsTh w="14%">OEM(s)</SettingsTh>
                                {activeTab.showInsurance && (
                                    <SettingsTh w="16%">Prohibited Insurance</SettingsTh>
                                )}
                                <SettingsTh w="90px" textAlign="center">Actions</SettingsTh>
                            </>
                        }
                    >
                        {currentRules.map(({ rule: part, index }) => (
                            // role="group" powers the chevron that fades in on
                            // hover for every editable cell in the row.
                            <Tr
                                key={`${part.partTerminologyId}-${part.positionGroupName}-${index}`}
                                role="group"
                                _hover={{ bg: 'gray.50' }}
                            >
                                <SettingsTd>
                                    <Checkbox
                                        size="sm"
                                        colorScheme="partbay-blue"
                                        isChecked={selectedIndexes.has(index)}
                                        onChange={(e) => handleToggleSelect(index, e.target.checked)}
                                        aria-label={`Select ${part.partTerminologyName}`}
                                    />
                                </SettingsTd>
                                <SettingsTd overflow="hidden">
                                    <VStack align="start" spacing={0}>
                                        <Text fontWeight="medium" fontSize="sm" noOfLines={1}>
                                            {part.partTerminologyName}
                                        </Text>
                                        <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                            {part.categoryName} → {part.subcategoryName}
                                        </Text>
                                    </VStack>
                                </SettingsTd>
                                <SettingsTd overflow="hidden">
                                    <InlinePositionCell
                                        value={part.positionGroupName}
                                        options={positionOptionsByPart.get(part.partTerminologyId)}
                                        ariaLabel={`Edit ${part.partTerminologyName} position`}
                                        onSelect={(next) => updateRuleAt(index, next)}
                                    />
                                </SettingsTd>
                                <SettingsTd overflow="hidden">
                                    <InlinePartTypesCell
                                        value={part.allowablePartTypes ?? []}
                                        ariaLabel={`Edit ${part.partTerminologyName} part types`}
                                        onSave={(next) =>
                                            updateRuleAt(index, { allowablePartTypes: next })
                                        }
                                    />
                                </SettingsTd>
                                <SettingsTd overflow="hidden">
                                    <InlineRuleMakesCell
                                        value={part.makes ?? 'all'}
                                        options={oemOptions}
                                        ariaLabel={`Edit ${part.partTerminologyName} OEMs`}
                                        onSave={(next) => updateRuleAt(index, { makes: next })}
                                    />
                                </SettingsTd>
                                {activeTab.showInsurance && (
                                    <SettingsTd overflow="hidden">
                                        <InlineRuleInsuranceCell
                                            value={part.prohibitedInsurance}
                                            ariaLabel={`Edit ${part.partTerminologyName} prohibited insurance`}
                                            onSave={(next) =>
                                                updateRuleAt(index, { prohibitedInsurance: next })
                                            }
                                        />
                                    </SettingsTd>
                                )}
                                <SettingsTd textAlign="center">
                                    <SettingsRowActions
                                        onEdit={() => openFlow(`?rule=${index}`)}
                                        onDelete={() => setRemoveIndexes([index])}
                                        editLabel="Edit part"
                                        deleteLabel="Remove part"
                                    />
                                </SettingsTd>
                            </Tr>
                        ))}
                    </SettingsTable>

                    <SettingsTableFooter>
                        <Pagination
                            currentPage={page}
                            totalPages={totalPages}
                            onPageChange={setPage}
                            py={0}
                        />
                    </SettingsTableFooter>
                </>
            )}

            <BulkActionBar
                selectedCount={selectedCount}
                actions={bulkActions}
                onClearSelection={() => setSelectedIndexes(new Set())}
                onAction={(actionId) => {
                    if (actionId === 'remove') {
                        setRemoveIndexes(Array.from(selectedIndexes));
                        return;
                    }
                    setBulkMode(actionId as PartAllowanceBulkMode);
                }}
                // Staged edits float their own save bar; stack above it.
                bottom={isDirty ? 20 : 4}
            />

            <PartAllowanceBulkModal
                mode={bulkMode}
                selectedCount={selectedCount}
                sharedPositionGroups={sharedPositionGroups}
                oemOptions={oemOptions}
                moveTargets={moveTargets}
                onApply={handleBulkApply}
                onClose={() => setBulkMode(null)}
            />

            <AlertDialog
                isOpen={removeIndexes !== null}
                leastDestructiveRef={removeCancelRef}
                onClose={() => setRemoveIndexes(null)}
                isCentered
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="md">
                            {removeIndexes?.length === 1 ? 'Remove part' : 'Remove parts'}
                        </AlertDialogHeader>
                        <AlertDialogCloseButton />
                        <AlertDialogBody>
                            <Text fontSize="sm" color="gray.600">
                                This removes{' '}
                                <Text as="span" fontWeight="semibold" color="gray.800">
                                    {removeIndexes?.length === 1
                                        ? rules[removeIndexes[0]]?.partTerminologyName ?? 'this part'
                                        : `${removeIndexes?.length ?? 0} parts`}
                                </Text>{' '}
                                from {activeTab.title}. You can add{' '}
                                {removeIndexes?.length === 1 ? 'it' : 'them'} back at any time.
                            </Text>
                        </AlertDialogBody>
                        <AlertDialogFooter gap={2}>
                            <Button
                                ref={removeCancelRef}
                                size="sm"
                                variant="outlineGray"
                                onClick={() => setRemoveIndexes(null)}
                            >
                                Cancel
                            </Button>
                            <Button size="sm" colorScheme="red" onClick={handleConfirmRemove}>
                                Remove
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    );
};

export default PartAllowancesSection;
